import { IconSettings } from '@myplayer/ui';
import { useEffect, useState, type ReactElement } from 'react';
import { ApiKeysSection, type ApiKeysSectionProps } from './api-keys-section.js';
import { useAuth } from './auth-context.js';
import { BrandQrSection, type BrandQrSectionProps } from './brand-qr-section.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardIcon,
  CardTitle,
  SegmentedControl,
} from './components/ui/primitives.js';
import { DomainsPage, type DomainsPageProps } from './domains-page.js';
import { IntegrationsSection, type IntegrationsSectionProps } from './integrations-section.js';
import { MembersSection, type MembersSectionProps } from './members-section.js';
import {
  OrganisationProfileSection,
  type OrganisationProfileSectionProps,
} from './organisation-section.js';
import { formatCapabilityRestrictionNotice } from './rbac-ui.js';
import { SignInPage } from './sign-in-page.js';
import {
  WorkspaceSettingsSection,
  type WorkspaceSettingsSectionProps,
} from './workspace-settings-section.js';
import { WorkspacesSection, type WorkspacesSectionProps } from './workspaces-section.js';

export { OrganisationProfileSection, SignInPage };

export type SettingsTabId =
  'workspace' | 'brand-qr' | 'members' | 'api-keys' | 'domains' | 'integrations' | 'organisation';

const SETTINGS_TABS: readonly {
  readonly id: SettingsTabId;
  readonly label: string;
  readonly scope: 'WORKSPACE' | 'ORGANISATION';
}[] = [
  { id: 'workspace', label: 'Workspace', scope: 'WORKSPACE' },
  { id: 'brand-qr', label: 'Brand QR', scope: 'WORKSPACE' },
  { id: 'integrations', label: 'Integrations', scope: 'WORKSPACE' },
  { id: 'organisation', label: 'Workspaces', scope: 'ORGANISATION' },
  { id: 'domains', label: 'Domains', scope: 'ORGANISATION' },
  { id: 'members', label: 'Members', scope: 'ORGANISATION' },
  { id: 'api-keys', label: 'API keys', scope: 'ORGANISATION' },
];

function isSettingsTabId(val: string | null): val is SettingsTabId {
  return SETTINGS_TABS.some((t) => t.id === val);
}

export type SettingsPageProps = MembersSectionProps &
  OrganisationProfileSectionProps &
  WorkspacesSectionProps &
  WorkspaceSettingsSectionProps &
  ApiKeysSectionProps &
  DomainsPageProps &
  IntegrationsSectionProps &
  BrandQrSectionProps & {
    readonly initialTab?: SettingsTabId;
  };

export function SettingsPage(props: SettingsPageProps = {}): ReactElement {
  const { session, can, canSpec, workspaceScope, setWorkspaceScope } = useAuth();
  const isAllWorkspaces = workspaceScope === 'ALL';
  const canUpdateWorkspace = can('workspace:update');
  const canManageIntegrations =
    can('domain:manage') || canSpec('configureCustomDomainsAndConnectors');
  const canAccessSettings = canUpdateWorkspace || canManageIntegrations;
  const queryTab =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('tab') : null;
  const isBrandPath = typeof window !== 'undefined' && window.location.pathname === '/brand';

  const visibleTabs = SETTINGS_TABS.filter((tab) =>
    isAllWorkspaces ? tab.scope === 'ORGANISATION' : tab.scope === 'WORKSPACE',
  );

  const resolvedDefaultTab: SettingsTabId =
    props.initialTab ??
    (isSettingsTabId(queryTab)
      ? queryTab
      : isBrandPath && !isAllWorkspaces
        ? 'brand-qr'
        : isAllWorkspaces
          ? 'organisation'
          : canUpdateWorkspace
            ? 'workspace'
            : 'integrations');
  const [activeTab, setActiveTab] = useState<SettingsTabId>(resolvedDefaultTab);

  useEffect(() => {
    const requestedTab = props.initialTab ?? (isSettingsTabId(queryTab) ? queryTab : null);
    if (
      requestedTab &&
      SETTINGS_TABS.find((t) => t.id === requestedTab)?.scope === 'ORGANISATION' &&
      !isAllWorkspaces
    ) {
      setWorkspaceScope('ALL');
      setActiveTab(requestedTab);
      return;
    }
    if (!visibleTabs.some((t) => t.id === activeTab)) {
      setActiveTab(
        isAllWorkspaces ? 'organisation' : canUpdateWorkspace ? 'workspace' : 'integrations',
      );
    }
  }, [
    props.initialTab,
    queryTab,
    isAllWorkspaces,
    visibleTabs,
    activeTab,
    canUpdateWorkspace,
    setWorkspaceScope,
  ]);

  if (!canAccessSettings) {
    return (
      <Card data-testid="settings-permission-denied">
        <CardHeader>
          <div className="flex items-center gap-3">
            <CardIcon>
              <IconSettings />
            </CardIcon>
            <div>
              <CardTitle>Settings</CardTitle>
              <CardDescription>Insufficient permissions</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-[14px] text-fg-secondary">
            {formatCapabilityRestrictionNotice({
              capability: 'workspace:update',
              actionSummary: 'modify workspace settings, team memberships, and Partner API keys',
              callerRole: session?.personaRole,
            })}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-page-title">
          {isAllWorkspaces ? 'Organisation Settings' : 'Workspace Settings'}
        </h1>
        <SegmentedControl
          role="tablist"
          ariaLabel="Settings sections"
          data-testid="settings-tabs"
          value={activeTab}
          onChange={setActiveTab}
          items={visibleTabs.map((tab) => ({
            value: tab.id,
            label: tab.label,
            testId: `settings-tab-${tab.id}`,
          }))}
        />
      </div>

      {activeTab === 'workspace' && (
        <div className="space-y-6" data-testid="settings-panel-workspace">
          <WorkspaceSettingsSection {...props} />
        </div>
      )}
      {activeTab === 'brand-qr' && (
        <div data-testid="settings-panel-brand-qr">
          <BrandQrSection {...props} />
        </div>
      )}
      {activeTab === 'members' && (
        <div data-testid="settings-panel-members">
          <MembersSection {...props} />
        </div>
      )}
      {activeTab === 'organisation' && (
        <div className="space-y-6" data-testid="settings-panel-organisation">
          <OrganisationProfileSection {...props} />
          <WorkspacesSection {...props} />
        </div>
      )}
      {activeTab === 'domains' && (
        <div data-testid="settings-panel-domains">
          <DomainsPage {...props} embedded />
        </div>
      )}
      {activeTab === 'api-keys' && (
        <div data-testid="settings-panel-api-keys">
          <ApiKeysSection {...props} showReadOnlyNoticeWhenUnauthorized />
        </div>
      )}
      {activeTab === 'integrations' && (
        <div data-testid="settings-panel-integrations">
          <IntegrationsSection {...props} />
        </div>
      )}
    </div>
  );
}
